import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  Button,
  FormField,
  Select,
  SpaceBetween,
  Form,
  Container,
  ColumnLayout,
  Checkbox,
  ExpandableSection,
  Input,
  Box,
} from "@cloudscape-design/components-themed";
import {
  COMMON_LABELS,
  DivisionDict,
  addBlank,
  getDivisionItems,
  getDivisionLabel,
} from "../default-constants";
import {
  COMPREHENSIVE_RESULT_STATUS,
  EN_ROUTE_RESULT_STATUS,
  EXAMINATION_STATUS,
  LABELS,
  PLACE_HOLDER,
  VM_ORDER_TARGET_CODE_STATUS,
} from "./constants";
import {
  isHalfNumberAndCharValid,
  isHalfNumberValid,
  isLengthValid,
  isHalfNumberListValid,
  isHalfCharListValid,
} from "../../utils/inputValidation";
import { getMessage } from "../../features/error/messages";
import {
  ComprehensiveCompanySearchRequest,
  Division,
} from "../../features/api-interfaces";
import {
  useGetComprehensiveCompaniesQuery,
  useGetDivisionQuery,
} from "../../features/api";
import { getComprehensiveMerchantCodeItems } from "../../utils/common";
import {
  INITIAL_SEARCH_CRITERIA,
  setSearchCriteria,
} from "../../features/slices/examinations-status-list";
import { useAppDispatch, useAppSelector } from "../../app/hook";
import { DetailSearchCorporateInfo } from "./DetailSearchCorporateInfo";
import { DetailSearchTargetCondition } from "./DetailSearchTargetCondition";
import { ClickDetail } from "@cloudscape-design/components-themed/internal/events";
import { RootState } from "../../app/store";

interface ISearchBar {
  setSkip: Dispatch<SetStateAction<boolean>>;
}

/**
 * 審査状況・POS登録状況照会 検索エリア
 */
const SearchBar = (props: ISearchBar) => {
  const { setSkip } = props;
  const { searchCriteria } = useAppSelector(
    (state: RootState) => state.examinationsStatusList
  );
  // 包括加盟店データを取得
  const comprehensiveCompanySearchRequest: ComprehensiveCompanySearchRequest = {
    companyCode: "",
    invisibleComprehensiveCompanyNoList: [],
  };
  const { data: comprehensiveMerchantListItem } =
    useGetComprehensiveCompaniesQuery(comprehensiveCompanySearchRequest);
  const comprehensiveMerchantListItems: DivisionDict[] = addBlank(
    getComprehensiveMerchantCodeItems(comprehensiveMerchantListItem)
  );

  // 共通データを取得
  // 区分取得API呼び出し(親区分コード：3:契約サービス区分コード)
  const divisionData = useGetDivisionQuery("3");
  const contractServiceDivisions = divisionData?.data as Division[];
  const contractServiceKbnItems: DivisionDict[] = addBlank(
    getDivisionItems(contractServiceDivisions)
  );

  // サービス名一覧
  const [serviceNameList, setServiceNameList] = useState<DivisionDict[]>([]);
  const [searchInput, setSearchInput] = useState(INITIAL_SEARCH_CRITERIA);
  const [comprehensiveMerchantList, setComprehensiveMerchantList] = useState<
    DivisionDict[]
  >([]);
  //入力チェックエラーメッセージ
  const [examAppNoErrorMsg, setExamAppNoErrorMsg] = useState("");
  const [appNoErrorMsg, setAppNoErrorMsg] = useState("");
  const [corporateNumberErrorMsg, setCorporateNumberErrorMsg] = useState("");
  const [companyNameErrorMsg, setCompanyNameErrorMsg] = useState("");
  const [merchantNameErrorMsg, setMerchantNameErrorMsg] = useState("");
  const [vm, setVm] = useState(false);
  const [settlementGroupIdErrorMsg, setSettlementGroupIdErrorMsg] =
    useState("");
  const [accessKeyErrorMsg, setAccessKeyErrorMsg] = useState("");

  //加盟店審査申請状況非活性フラグ
  const [applicationDisableFlg, setApplicationDisableFlg] = useState(false);

  //POS登録状況非活性フラグ
  const [posDisableFlg, setPosDisableFlg] = useState(true);

  //ドコモ審査非活性フラグ
  const [docomoDisableFlg, setDocomoDisableFlg] = useState(true);

  useEffect(() => {
    setSearchInput(searchCriteria);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchCriteria]);

  useEffect(() => {
    setComprehensiveMerchantList(comprehensiveMerchantListItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comprehensiveMerchantListItem]);

  useEffect(() => {
    setVm(searchInput.smccFlg || searchInput.tfFlg || searchInput.saisonFlg);
  }, [searchInput.smccFlg, searchInput.tfFlg, searchInput.saisonFlg]);

  useEffect(() => {
    setServiceNameList(contractServiceKbnItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divisionData]);

  const dispatch = useAppDispatch();

  // エラーメッセージ
  const [errText, setErrText] = useState<string>();

  /**
   * 審査申請番号のチェック
   * @param {string} examNumbersString
   * @return {boolean}
   */
  const checkExaminationApplicationNo = (examNumbersString: string) => {
    if (!isHalfCharListValid(examNumbersString, ",")) {
      setExamAppNoErrorMsg(
        getMessage(
          "KGRERR032",
          "審査申請番号",
          PLACE_HOLDER.EXAMINATION_APPLICATION_NO
        ).label
      );
      return false;
    }
    setExamAppNoErrorMsg("");
    return true;
  };

  /**
   * 申請番号のチェック
   * @param {string} appNumbersString
   * @return {boolean}
   */
  const checkApplicationNo = (appNumbersString: string) => {
    if (!isHalfNumberListValid(appNumbersString, ",")) {
      setAppNoErrorMsg(
        getMessage("KGRERR032", "申請番号", PLACE_HOLDER.APPLICATION_NO).label
      );
      return false;
    }
    setAppNoErrorMsg("");
    return true;
  };

  /**
   * 法人番号のチェック
   * @param {string} corporateNumber
   * @return {boolean}
   */
  const checkCorporateNumber = (corporateNumber: string) => {
    if (!isHalfNumberValid(corporateNumber)) {
      setCorporateNumberErrorMsg(getMessage("KGRERR006", "法人番号").label);
      return false;
    } else if (!isLengthValid(corporateNumber, 13)) {
      setCorporateNumberErrorMsg(getMessage("KGRERR059", 13).label);
      return false;
    }
    setCorporateNumberErrorMsg("");
    return true;
  };

  /**
   * 事業者名のチェック
   * @param {string} companyName
   * @returns {boolean}
   */
  const checkCompanyName = (companyName: string) => {
    if (!isLengthValid(companyName, 30)) {
      setCompanyNameErrorMsg(getMessage("KGRERR058", 30).label);
      return false;
    }
    setCompanyNameErrorMsg("");
    return true;
  };

  /**
   * 加盟店名のチェック
   * @param {string} merchantName
   * @returns {boolean}
   */
  const checkMerchantName = (merchantName: string) => {
    if (!isLengthValid(merchantName, 100)) {
      setMerchantNameErrorMsg(getMessage("KGRERR058", 100).label);
      return false;
    }
    setMerchantNameErrorMsg("");
    return true;
  };

  /**
   * クリアボタンを押したときの処理.
   */
  const onClear = () => {
    setSkip(true);
    setSearchInput(INITIAL_SEARCH_CRITERIA);
    setExamAppNoErrorMsg("");
    setAppNoErrorMsg("");
    setCorporateNumberErrorMsg("");
    setCompanyNameErrorMsg("");
    setMerchantNameErrorMsg("");
    setApplicationDisableFlg(false);
    setPosDisableFlg(true);
    setDocomoDisableFlg(true);
  };

  /**
   * 決裁グループIDのチェック
   * @param {string} settlementGroupId
   * @returns {boolean}
   */
  const checkSettlementGroupId = (settlementGroupId: string) => {
    if (!isHalfNumberAndCharValid(settlementGroupId)) {
      setSettlementGroupIdErrorMsg(getMessage("KGRERR164", "半角英数字").label);
      return false;
    } else if (!isLengthValid(settlementGroupId, 26)) {
      setSettlementGroupIdErrorMsg(getMessage("KGRERR058", 26).label);
      return false;
    }
    setSettlementGroupIdErrorMsg("");
    return true;
  };

  /**
   * Access Keyのチェック
   * @param {string} accessKey
   * @returns {boolean}
   */
  const checkAccessKey = (accessKey: string) => {
    if (!isHalfNumberAndCharValid(accessKey)) {
      setAccessKeyErrorMsg(getMessage("KGRERR164", "半角英数字").label);
      return false;
    } else if (!isLengthValid(accessKey, 26)) {
      setAccessKeyErrorMsg(getMessage("KGRERR058", 26).label);
      return false;
    }
    setAccessKeyErrorMsg("");
    return true;
  };

  /**
   * 検索ボタンイベント
   */
  const handleOnSubmit = (e: CustomEvent<ClickDetail>) => {
    // イベントキャンセル
    e.preventDefault();

    // Validate
    if (
      !checkExaminationApplicationNo(
        searchInput.examinationApplicationNoList.join()
      )
    )
      return;
    if (!checkApplicationNo(searchInput.applicationNoList.join())) return;
    if (!checkCorporateNumber(searchInput.corporateNumber)) return;
    if (!checkCompanyName(searchInput.companyName)) return;
    if (!checkMerchantName(searchInput.merchantName)) return;
    setSkip(false);
    dispatch(
      setSearchCriteria({
        ...searchInput,
        timestamp: new Date().getTime(),
      })
    );
    //エラーメッセージクリア
    setErrText("");
  };

  /**
   * 加盟店審査申請状況選択時、POS登録状況とドコモ審査の値を初期値に戻す
   */
  const changeMerchantApplicationCondition = () => {
    setApplicationDisableFlg(false);
    setPosDisableFlg(true);
    setDocomoDisableFlg(true);
    setSearchInput({
      ...searchInput,
      checkTargetCode: 1,
      posRegisterApplicationStartDate: "",
      posRegisterApplicationEndDate: "",
      posBeforeApplicationFlg: false,
      posUnderApplicationFlg: false,
      posCompletedApplicationFlg: false,
      posAcquirer: "1",
      docomoExaminationApplicationStartDate: "",
      docomoExaminationApplicationEndDate: "",
      docomoBeforeExaminationFlg: false,
      docomoUnderExaminationFlg: false,
      docomoCompletedExaminationFlg: false,
      docomoNormalExaminationFlg: false,
      docomoTenantExaminationFlg: false,
    });
  };

  /**
   * POS登録状況選択時、加盟店審査申請状況とドコモ審査の値を初期値に戻す
   */
  const changePosRegistrationCondition = () => {
    setApplicationDisableFlg(true);
    setPosDisableFlg(false);
    setDocomoDisableFlg(true);
    setVm(false);
    setSearchInput({
      ...searchInput,
      checkTargetCode: 2,
      examinationApplicationStartDate: "",
      examinationApplicationEndDate: "",
      contractRoute: "1",
      contractRouteName: "",
      settlementGroupId: "",
      accessKey: "",
      acquirer: "1",
      smccFlg: false,
      tfFlg: false,
      saisonFlg: false,
      jcbFlg: false,
      amexFlg: false,
      creditBeforeExaminationStatusFlg: false,
      creditUnderExaminationStatusFlg: false,
      creditCompletedExaminationStatusFlg: false,
      normalExaminationFlg: false,
      tenantExaminationFlg: false,
      docomoExaminationApplicationStartDate: "",
      docomoExaminationApplicationEndDate: "",
      examinationTypeNewExaminationFlg: false,
      examinationTypeChangeExaminationFlg: false,
      examinationTypeCancellationFlg: false,
      docomoBeforeExaminationFlg: false,
      docomoUnderExaminationFlg: false,
      docomoCompletedExaminationFlg: false,
      docomoNormalExaminationFlg: false,
      docomoTenantExaminationFlg: false,
    });
  };

  /**
   * ドコモ審査選択時、加盟店審査申請状況とPOS登録状況の値を初期値に戻す
   */
  const changeDocomoRegistrationCondition = () => {
    setApplicationDisableFlg(true);
    setPosDisableFlg(true);
    setDocomoDisableFlg(false);
    setVm(false);
    setSearchInput({
      ...searchInput,
      checkTargetCode: 3,
      posRegisterApplicationStartDate: "",
      posRegisterApplicationEndDate: "",
      posBeforeApplicationFlg: false,
      posUnderApplicationFlg: false,
      posCompletedApplicationFlg: false,
      posAcquirer: "1",
      examinationApplicationStartDate: "",
      examinationApplicationEndDate: "",
      contractRoute: "1",
      contractRouteName: "",
      settlementGroupId: "",
      accessKey: "",
      acquirer: "1",
      smccFlg: false,
      tfFlg: false,
      saisonFlg: false,
      jcbFlg: false,
      amexFlg: false,
      creditBeforeExaminationStatusFlg: false,
      creditUnderExaminationStatusFlg: false,
      creditCompletedExaminationStatusFlg: false,
      examinationTypeNewExaminationFlg: false,
      examinationTypeChangeExaminationFlg: false,
      examinationTypeCancellationFlg: false,
      normalExaminationFlg: false,
      tenantExaminationFlg: false,
    });
  };

  /**
   * 「NTTデータ以外」のラジオボタン押下時処理
   */
  const changeOrderTargetExceptingNttf = (inputValue: string) => {
    if (inputValue === "3") {
      setVm(true);
    }
  };

  /**
   * 「NTTデータ」のラジオボタン押下時処理
   */
  const changeOrderTarget = (inputValue: string) => {
    if (inputValue !== "3") {
      setVm(false);
    }
  };

  /**
   * vm 選択時処理
   */
  const changeOrderTargetVm = (inputValue: boolean) => {
    setVm(inputValue);
  };

  return (
    <Form
      actions={
        <Button variant={"normal"} onClick={handleOnSubmit}>
          {COMMON_LABELS.BTNS.SEARCH}
        </Button>
      }
      errorText={errText}
    >
      <Container
        header={
          <ColumnLayout>
            <Box float={"right"}>
              <Button variant={"normal"} onClick={onClear}>
                {COMMON_LABELS.BTNS.CLEAR}
              </Button>
            </Box>
          </ColumnLayout>
        }
      >
        <SpaceBetween direction={"vertical"} size={"l"}>
          <ColumnLayout columns={4}>
            <FormField label={LABELS.SERVICE_NAME}>
              <Select
                selectedOption={{
                  value: searchInput.contractServiceCode,
                  label: getDivisionLabel(
                    serviceNameList,
                    searchInput.contractServiceCode
                  ),
                }}
                options={serviceNameList}
                filteringType="none"
                onChange={({ detail: { selectedOption } }) =>
                  setSearchInput({
                    ...searchInput,
                    contractServiceCode: selectedOption.value!,
                  })
                }
              />
            </FormField>
            <FormField
              label={LABELS.EXAMINATION_APPLICATION_NO}
              constraintText={PLACE_HOLDER.DELIMITED_BY_COMMA}
              errorText={examAppNoErrorMsg}
            >
              <Input
                value={searchInput.examinationApplicationNoList.join()}
                placeholder={PLACE_HOLDER.EXAMINATION_APPLICATION_NO}
                onChange={({ detail: { value } }) => {
                  setSearchInput({
                    ...searchInput,
                    examinationApplicationNoList: value ? value.split(",") : [],
                  });
                  checkExaminationApplicationNo(value);
                }}
              />
            </FormField>
            <FormField
              label={LABELS.APPLICATION_NO}
              constraintText={PLACE_HOLDER.DELIMITED_BY_COMMA}
              errorText={appNoErrorMsg}
            >
              <Input
                value={searchInput.applicationNoList.join()}
                placeholder={PLACE_HOLDER.APPLICATION_NO}
                onChange={({ detail: { value } }) => {
                  setSearchInput({
                    ...searchInput,
                    applicationNoList: value ? value.split(",") : [],
                  });
                  checkApplicationNo(value);
                }}
              />
            </FormField>
          </ColumnLayout>
          <FormField label={LABELS.EXAMINATION_STATUS}>
            <SpaceBetween direction={"horizontal"} size={"l"}>
              <Checkbox
                checked={searchInput.beforeExaminationStatusFlg}
                onChange={({ detail: { checked } }) =>
                  setSearchInput({
                    ...searchInput,
                    beforeExaminationStatusFlg: checked,
                  })
                }
              >
                {EXAMINATION_STATUS.BEFORE}
              </Checkbox>
              <Checkbox
                checked={searchInput.underExaminationStatusFlg}
                onChange={({ detail: { checked } }) =>
                  setSearchInput({
                    ...searchInput,
                    underExaminationStatusFlg: checked,
                  })
                }
              >
                {EXAMINATION_STATUS.APPLYING}
              </Checkbox>
              <Checkbox
                checked={searchInput.completedExaminationStatusFlg}
                onChange={({ detail: { checked } }) =>
                  setSearchInput({
                    ...searchInput,
                    completedExaminationStatusFlg: checked,
                  })
                }
              >
                {EXAMINATION_STATUS.COMPLETED}
              </Checkbox>
            </SpaceBetween>
          </FormField>
          <FormField label={LABELS.COMPREHENSIVE_RESULT}>
            <SpaceBetween direction={"horizontal"} size={"l"}>
              <Checkbox
                checked={searchInput.comprehensiveExaminationOkFlg}
                onChange={({ detail: { checked } }) =>
                  setSearchInput({
                    ...searchInput,
                    comprehensiveExaminationOkFlg: checked,
                  })
                }
              >
                {COMPREHENSIVE_RESULT_STATUS.OK}
              </Checkbox>
              <Checkbox
                checked={searchInput.comprehensiveExaminationNgFlg}
                onChange={({ detail: { checked } }) =>
                  setSearchInput({
                    ...searchInput,
                    comprehensiveExaminationNgFlg: checked,
                  })
                }
              >
                {COMPREHENSIVE_RESULT_STATUS.NG}
              </Checkbox>
              <Checkbox
                checked={
                  searchInput.comprehensiveExaminationWaitingForRequestFlg
                }
                onChange={({ detail: { checked } }) =>
                  setSearchInput({
                    ...searchInput,
                    comprehensiveExaminationWaitingForRequestFlg: checked,
                  })
                }
              >
                {COMPREHENSIVE_RESULT_STATUS.WAITING_FOR_REQUEST}
              </Checkbox>
              <Checkbox
                checked={searchInput.comprehensiveExaminationApprovalPendingFlg}
                onChange={({ detail: { checked } }) =>
                  setSearchInput({
                    ...searchInput,
                    comprehensiveExaminationApprovalPendingFlg: checked,
                  })
                }
              >
                {COMPREHENSIVE_RESULT_STATUS.APPROVAL_PENDING}
              </Checkbox>
            </SpaceBetween>
          </FormField>
          <FormField label={LABELS.VM_ORDER_TARGET_CODE}>
            <SpaceBetween direction={"horizontal"} size={"l"}>
              <Checkbox
                checked={searchInput.vmOrderTargetCodeFlg}
                onChange={({ detail: { checked } }) =>
                  setSearchInput({
                    ...searchInput,
                    vmOrderTargetCodeFlg: checked,
                  })
                }
              >
                {VM_ORDER_TARGET_CODE_STATUS.DECIDED}
              </Checkbox>
              <Checkbox
                checked={searchInput.vmOrderTargetCodeNullFlg}
                onChange={({ detail: { checked } }) =>
                  setSearchInput({
                    ...searchInput,
                    vmOrderTargetCodeNullFlg: checked,
                  })
                }
              >
                {VM_ORDER_TARGET_CODE_STATUS.UNDECIDED}
              </Checkbox>
            </SpaceBetween>
          </FormField>
          <FormField label={LABELS.EN_ROUTE_RESULT}>
            <SpaceBetween direction={"horizontal"} size={"l"}>
              <Checkbox
                checked={searchInput.enRouteExaminationNgAdsFlg}
                onChange={({ detail: { checked } }) =>
                  setSearchInput({
                    ...searchInput,
                    enRouteExaminationNgAdsFlg: checked,
                  })
                }
              >
                {EN_ROUTE_RESULT_STATUS.ADS_NG}
              </Checkbox>
              <Checkbox
                checked={searchInput.enRouteExaminationNgOmniFlg}
                onChange={({ detail: { checked } }) =>
                  setSearchInput({
                    ...searchInput,
                    enRouteExaminationNgOmniFlg: checked,
                  })
                }
              >
                {EN_ROUTE_RESULT_STATUS.OMNI_NG}
              </Checkbox>
            </SpaceBetween>
          </FormField>
          <FormField label={LABELS.NTT_FIXED_CREDIT}>
            <Checkbox
              checked={searchInput.nttFixedCreditNgFlg}
              onChange={({ detail: { checked } }) =>
                setSearchInput({
                  ...searchInput,
                  nttFixedCreditNgFlg: checked,
                })
              }
            >
              {COMPREHENSIVE_RESULT_STATUS.NG}
            </Checkbox>
          </FormField>
          <ExpandableSection headerText={LABELS.DETAIL_SEARCH}>
            <SpaceBetween direction={"vertical"} size={"l"}>
              <DetailSearchCorporateInfo
                searchInput={searchInput}
                setSearchInput={setSearchInput}
                corporateNumberErrorMsg={corporateNumberErrorMsg}
                checkCorporateNumber={checkCorporateNumber}
                companyNameErrorMsg={companyNameErrorMsg}
                checkCompanyName={checkCompanyName}
                merchantNameErrorMsg={merchantNameErrorMsg}
                checkMerchantName={checkMerchantName}
                comprehensiveMerchantList={comprehensiveMerchantList}
              />
              <DetailSearchTargetCondition
                searchInput={searchInput}
                setSearchInput={setSearchInput}
                changeMerchantApplicationCondition={
                  changeMerchantApplicationCondition
                }
                applicationDisableFlg={applicationDisableFlg}
                posDisableFlg={posDisableFlg}
                docomoDisableFlg={docomoDisableFlg}
                checkSettlementGroupId={checkSettlementGroupId}
                settlementGroupIdErrorMsg={settlementGroupIdErrorMsg}
                checkAccessKey={checkAccessKey}
                accessKeyErrorMsg={accessKeyErrorMsg}
                changeOrderTargetExceptingNttf={changeOrderTargetExceptingNttf}
                changeOrderTarget={changeOrderTarget}
                vm={vm}
                setVm={setVm}
                changeOrderTargetVm={changeOrderTargetVm}
                changePosRegistrationCondition={changePosRegistrationCondition}
                changeDocomoRegistrationCondition={
                  changeDocomoRegistrationCondition
                }
              />
            </SpaceBetween>
          </ExpandableSection>
        </SpaceBetween>
      </Container>
    </Form>
  );
};

export default SearchBar;
